import { ContentCopy, InfoOutlined } from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { Tooltip } from "../../../components";
import { LabelValue } from "../../../components/LabelValue";
import { useLocalization } from "../../../hooks/useLocalization";
import { CompanyLogo } from "./CompanyLogo";

type Props = {
  initials: string;
  organizationName: string;
  organizationKey: string;
  internalUsersCount: number;
  organizationLogoUrl?: string;
};

const DESKTOP_LABEL_RAIL = 152;
const VALUE_GAP = 16;

export const CompanyInfo: React.FC<Props> = ({
  initials,
  organizationName,
  organizationKey,
  internalUsersCount,
  organizationLogoUrl,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [copied, setCopied] = useState(false);

  const LOGO_W = 232;
  const LOGO_H = 138;

  const handleCopyKey = (keyToCopy: string) => {
    void navigator.clipboard.writeText(keyToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Stack
      spacing={0}
      sx={{
        "& > :not(style) ~ :not(style)": { mt: 0 },
        width: "100%",
        minWidth: 0,
      }}
    >
      {/* Mobile layout */}
      {!mdUp && (
        <>
          <CompanyLogo
            aspectRatio="16 / 9"
            initials={initials}
            isMobile
            maxWidth={260}
            organizationLogoUrl={organizationLogoUrl}
            organizationName={organizationName}
            width="100%"
          />

          <Box sx={{ width: "100%", minWidth: 0, mt: "24px !important" }}>
            <LabelValue
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {t("company.companyInfo.companyKey")}
                  <Tooltip
                    message={t("company.companyInfo.companyKeyTooltip")}
                    placement="right"
                  >
                    <InfoOutlined
                      sx={{
                        fontSize: 16,
                        color: theme.colors.text[1],
                        display: "block",
                      }}
                    />
                  </Tooltip>
                </Box>
              }
              showSuccessIndicator={copied}
              successMessage={t("company.companyInfo.copied")}
              value={undefined}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                  minWidth: 0,
                  color: theme.colors.text[5],
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                  title={organizationKey}
                >
                  {organizationKey}
                </span>
                <IconButton
                  aria-label={t("company.companyInfo.copyAriaLabel")}
                  onClick={() => handleCopyKey(organizationKey)}
                  size="small"
                  sx={{
                    color: theme.colors.text[1],
                    "&:hover": { color: theme.colors.primary[5] },
                    mt: 0.25,
                    flexShrink: 0,
                  }}
                >
                  <ContentCopy fontSize="inherit" />
                </IconButton>
              </Box>
            </LabelValue>
          </Box>

          {/* Divider */}
          <Divider
            sx={{ width: "100%", bgcolor: "#D1D1D6", mt: "24px !important" }}
          />

          {/* Usage Statistics Section Title */}
          <Box sx={{ width: "100%", pt: 2, mt: "0px !important" }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 18,
                lineHeight: "24px",
                letterSpacing: "0.02em",
                color: theme.colors.text[5] || "#1C1C1E",
              }}
            >
              {t("company.companyInfo.usageStatistics")}
            </Typography>
          </Box>

          {/* Usage Statistics Content */}
          <Stack
            spacing={1}
            sx={{ width: "100%", minWidth: 0, mt: "24px !important" }}
          >
            <LabelValue
              label={t("company.companyInfo.internalUsers")}
              value={internalUsersCount}
            />
          </Stack>

          {/* Divider */}
          <Divider
            sx={{ width: "100%", bgcolor: "#D1D1D6", mt: "24px !important" }}
          />
        </>
      )}

      {/* Desktop layout */}
      {mdUp && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `${DESKTOP_LABEL_RAIL}px 1fr`,
            columnGap: `${VALUE_GAP}px`,
            alignItems: "start",
            rowGap: 1,
            justifyItems: "start",
          }}
        >
          {/* Logo */}
          <Box sx={{ gridColumn: "1 / -1", justifySelf: "start", mb: 1.5 }}>
            <CompanyLogo
              height={LOGO_H}
              initials={initials}
              organizationLogoUrl={organizationLogoUrl}
              organizationName={organizationName}
              width={LOGO_W}
            />
          </Box>

          {/* Metrics */}
          <Box sx={{ gridColumn: "1 / -1", width: "100%" }}>
            <Stack spacing={1} sx={{ mt: 0 }}>
              <LabelValue
                containerSx={{ width: "fit-content" }}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {t("company.companyInfo.companyKey")}
                    <Tooltip
                      message={t("company.companyInfo.companyKeyTooltip")}
                      placement="right"
                    >
                      <InfoOutlined
                        sx={{
                          fontSize: 16,
                          color: theme.colors.text[1],
                          display: "block",
                        }}
                      />
                    </Tooltip>
                  </Box>
                }
                showSuccessIndicator={copied}
                successMessage={t("company.companyInfo.copied")}
                value={undefined}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                    color: theme.colors.text[5],
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                    title={organizationKey}
                  >
                    {organizationKey}
                  </span>
                  <IconButton
                    aria-label={t("company.companyInfo.copyAriaLabel")}
                    onClick={() => handleCopyKey(organizationKey)}
                    size="small"
                    sx={{
                      color: theme.colors.text[1],
                      "&:hover": { color: theme.colors.primary[5] },
                      mt: 0.25,
                    }}
                  >
                    <ContentCopy fontSize="inherit" />
                  </IconButton>
                </Box>
              </LabelValue>

              <LabelValue
                label={t("company.companyInfo.users")}
                value={internalUsersCount}
              />
            </Stack>
          </Box>
        </Box>
      )}
    </Stack>
  );
};
